/* ==========================================================================
   Giorgio — a subscription that belongs to only some packaging values.

   The biscotti sells a subscription on the everyday Dozen only: a Gift Box is
   a present and a jar is a one-off, so neither should offer a repeat delivery.
   Over there that rule lives in the Seal app, whose selling plan is attached to
   the twelve "<flavour> / The Dozen / No Ribbon" variants and to nothing else,
   so Seal itself greys the row out on any other packaging. The cookies were set
   up as "Italian Cookies — all variants" instead, which is why the subscription
   follows the shopper onto the Gift Box and the Cookie Jar.

   The same rule is expressed here, in the theme, rather than by re-pointing the
   Seal rule at a hand-picked list of variant ids. Two reasons:

     - A variant list in an app is invisible from the theme editor and silently
       wrong the moment a flavour is added. This file is driven by an option
       VALUE typed into the variant picker's settings, so a new flavour needs no
       maintenance at all.
     - The client asked for the revisions to live in the theme so they are not
       re-done by hand later. An app setting would not survive that.

   What it does is exactly what Seal does on the biscotti — the classes are
   Seal's own, so Seal's stylesheet does the hiding and the result is identical
   down to the pixel:

     .sealsubs-container            -> sls-hide
     the subscribe row              -> sls-disabled-option-container
     the one-time row               -> selected, so the form posts no plan

   Reapplied through a MutationObserver because Seal re-renders its widget on
   every variant change and every price refresh, and each re-render arrives with
   the classes reset.
   ========================================================================== */
(function () {
  'use strict';

  var HOST = 'variant-selects[data-gio-sub-hide-when]';
  var CONTAINER = '.sealsubs-container';
  var ROW = '.sls-option-container';
  var PLAN_PICKER = '.sls-selling-plan-group-options';

  /* Seal's own classes. Borrowed rather than re-invented so that hiding looks
     the same here as it does on a product Seal itself excludes. */
  var HIDE_CONTAINER = 'sls-hide';
  var HIDE_ROW = 'sls-disabled-option-container';
  var ACTIVE_ROW = 'sls-active';

  function host() {
    return document.querySelector(HOST);
  }

  function values(el) {
    return (el.getAttribute('data-gio-sub-hide-when') || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /* Same addressing as giorgio-option-scope.js: the picker already puts
     `product-form-variant-<position>` on every option row. */
  function sourceValue(el) {
    var position = el.getAttribute('data-gio-sub-hide-source') || '2';
    var scope = el.querySelector('.product-form-variant-' + position);
    var input = scope && scope.querySelector('input[type="radio"]:checked');
    if (input) return input.value;

    /* A dropdown picker instead of pills. */
    var select = scope && scope.querySelector('select');
    return select ? select.value : null;
  }

  /* Returns true / false when this product is configured, and null when it is
     not. The difference matters: on a product with no setting this file has to
     keep its hands off entirely, because Seal marks its OWN excluded variants
     with these very classes and an unconditional "not hidden" would strip them
     back off -- which is what briefly happened to the biscotti Gift Box. */
  function shouldHide() {
    var el = host();
    if (!el) return null;
    var list = values(el);
    if (!list.length) return null;
    var current = sourceValue(el);
    if (current === null) return null;
    return list.indexOf(current) !== -1;
  }

  /* Adds or removes one of Seal's classes, and remembers on the element that
     WE were the one who added it.

     Both halves of that are needed. Skipping an element Seal has already hidden
     keeps two rules from fighting over the same row, and refusing to remove a
     class we did not put there is what stops this file from quietly re-enabling
     a subscription the app deliberately excluded.

     The guard also keeps the MutationObserver that calls this from firing on
     its own output for ever: a run that changes nothing writes nothing. */
  var OWNED = 'data-gg-sub-hid';

  function mark(el, cls, on) {
    if (on) {
      if (el.classList.contains(cls)) return;
      el.classList.add(cls);
      el.setAttribute(OWNED, cls);
    } else {
      if (el.getAttribute(OWNED) !== cls) return;
      el.classList.remove(cls);
      el.removeAttribute(OWNED);
    }
  }

  function rows(container) {
    return Array.prototype.slice.call(container.querySelectorAll(ROW));
  }

  /* The subscribe row is the one carrying the frequency picker. Matching on
     that rather than on position survives Seal offering several plan groups,
     and survives the one-time row being switched off in the app's settings. */
  function isSubscribe(row) {
    return !!row.querySelector(PLAN_PICKER);
  }

  function apply() {
    var container = document.querySelector(CONTAINER);
    if (!container) return;

    var all = rows(container);
    if (!all.length) return;

    var subscribe = all.filter(isSubscribe);
    var once = all.filter(function (r) { return subscribe.indexOf(r) === -1; })[0] || null;
    if (!subscribe.length) return;

    var hide = shouldHide();
    if (hide === null) return;

    mark(container, HIDE_CONTAINER, hide);
    subscribe.forEach(function (row) { mark(row, HIDE_ROW, hide); });

    if (!hide || !once) return;

    /* Hiding the row is not enough on its own: a shopper who chose a
       subscription on the Dozen and then switched to a Gift Box would keep the
       selling plan in a row they can no longer see or undo. Click rather than
       set .checked, so Seal's own handler clears the plan out of the form. */
    var chosen = subscribe.filter(function (row) {
      var input = row.querySelector('input[type="radio"]');
      return input && input.checked;
    })[0];
    if (!chosen) return;

    var input = once.querySelector('input[type="radio"]');
    if (input) {
      input.click();
    } else {
      once.click();
    }
    once.classList.add(ACTIVE_ROW);
    subscribe.forEach(function (row) { row.classList.remove(ACTIVE_ROW); });
  }

  function safeApply() {
    try {
      apply();
    } catch (err) {
      if (window.console) console.error('[gio-sub-scope]', err);
    }
  }

  /* setTimeout, not requestAnimationFrame: a rAF scheduled from inside a
     MutationObserver callback can be skipped entirely in a background tab,
     which would leave the latch stuck and the widget never corrected. The same
     note is on the observer in the gallery files. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      safeApply();
    }, 0);
  }

  /* Both the variant change and Seal's re-render show up as mutations under
     <body>, so one observer covers the pair. The subtree is small enough that
     watching attributes too is cheap, and it is needed: Dawn swaps the checked
     radio without touching the tree. */
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['checked', 'class', 'value'],
  });

  document.addEventListener('change', schedule);
  document.addEventListener('click', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeApply, { once: true });
  } else {
    safeApply();
  }
  window.addEventListener('load', safeApply);
})();
